import type {
  CogoCollabConflict,
  CogoCollabConflictReason,
  CogoCollabTransaction,
} from "./types";

const ACTIVE_STATUSES = new Set([
  "draft",
  "proposed",
  "accepted",
  "conflicted",
]);

function isActive(transaction: CogoCollabTransaction): boolean {
  return ACTIVE_STATUSES.has(transaction.status);
}

function sameBlockProperty(
  a: CogoCollabTransaction,
  b: CogoCollabTransaction,
): boolean {
  return Boolean(
    a.target.blockId &&
    b.target.blockId &&
    a.target.blockId === b.target.blockId &&
    a.target.propertyKey &&
    b.target.propertyKey &&
    a.target.propertyKey === b.target.propertyKey,
  );
}

function changedPaths(transaction: CogoCollabTransaction): Set<string> {
  return new Set(
    transaction.changes
      .flatMap((change) => [change.path, change.from])
      .filter((value): value is string => Boolean(value)),
  );
}

function conflictReason(
  local: CogoCollabTransaction,
  incoming: CogoCollabTransaction,
): CogoCollabConflictReason | null {
  if (!isActive(local) || !isActive(incoming)) return null;
  if (
    local.target.cogoJsonPath &&
    local.target.cogoJsonPath === incoming.target.cogoJsonPath
  ) {
    return "same-json-path";
  }
  if (sameBlockProperty(local, incoming)) return "same-block-property";

  const localPaths = changedPaths(local);
  for (const path of changedPaths(incoming)) {
    if (localPaths.has(path)) return "same-json-path";
  }

  if (
    local.changes.some((change) => change.op === "remove") &&
    incoming.target.cogoJsonPath &&
    localPaths.has(incoming.target.cogoJsonPath)
  ) {
    return "deleted-target";
  }

  return null;
}

export function userFacingConflictMessage(
  reason: CogoCollabConflictReason,
): string {
  switch (reason) {
    case "same-json-path":
    case "same-block-property":
      return "This part already has a pending change. Review both before applying.";
    case "deleted-target":
      return "The selected part changed since this suggestion was made.";
    case "stale-preview-anchor":
      return "The selected preview area is no longer available.";
    case "failed-validation":
      return "The suggestion needs review because validation did not pass.";
  }
}

export function detectCogoCollabConflicts(input: {
  existing: CogoCollabTransaction[];
  incoming: CogoCollabTransaction;
  createdAt?: string;
}): CogoCollabConflict[] {
  const createdAt = input.createdAt ?? input.incoming.createdAt;
  return input.existing.flatMap((local) => {
    const reason = conflictReason(local, input.incoming);
    if (!reason) return [];
    return [
      {
        id: `${local.id}__${input.incoming.id}__${reason}`,
        localId: local.id,
        incomingId: input.incoming.id,
        reason,
        message: userFacingConflictMessage(reason),
        createdAt,
      },
    ] satisfies CogoCollabConflict[];
  });
}

export function markTransactionConflicted(
  transaction: CogoCollabTransaction,
  conflicts: CogoCollabConflict[],
): CogoCollabTransaction {
  if (conflicts.length === 0) return transaction;
  return {
    ...transaction,
    status: "conflicted",
    updatedAt: conflicts[0].createdAt,
  };
}
